<template>
    <div class="loading-screen">
        <!-- Status panel on bottom left -->
		<transition name="fade">
			<div class="status-panel accent-container" v-if="showStatusPanel">
				<div v-for="(item, index) in activeStatusItems" :key="index" class="status-item">
					<span class="status-label">{{ item.label }}:</span>
					<span class="status-value" :class="{ 'error-value': item.error }">{{ item.value }}</span>
				</div>
			</div>
		</transition>

        <!-- Fixed content container -->
        <div class="fixed-content">
            <div class="loading-logo" ref="loadingLogo">
				<img class="logo" src="/images/Buzztime_tv_white.svg" alt="Buzztime">
            </div>
            <div class="loading-bar-container">
                <div class="loading-bar" ref="loadingBar" :style="{ width: `${progressPercentage}%` }"></div>
            </div>
        </div>

        <!-- Dynamic content container -->
        <div class="dynamic-content">
            <div class="status-messages-container">
                <div class="message-wrapper" ref="messageWrapper">
                    <transition
                        name="message-transition"
                        mode="out-in"
                        @enter="onMessageEnter"
                        @leave="onMessageLeave"
                    >
                        <div
                            :key="currentMessageId"
                            class="status-message"
                            :class="{ 'error-message': !currentMessageSuccess }"
                        >
                            {{ currentMessage }}
                        </div>
                    </transition>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, onMounted, watch, computed, onUnmounted, reactive} from 'vue';
import gsap from 'gsap';
import {loadingStatus, loadingStep, loadingSuccess, startupMode} from '../services/startup.js';
import {systemInfo} from "@/systemInfo.js";
import Header from "@/components/Header.vue";
import {getDeviceId, getOrCreateDeviceId} from "@/services/virtualDisplay.js";

const props = defineProps({
    loadingStatusProp: {
        type: String,
        default: 'Loading...'
    }
});

const emit = defineEmits(['loading-complete']);

// Refs
const loadingLogo = ref(null);
const loadingBar = ref(null);
const messageWrapper = ref(null);
const showStatusPanel = ref(false);


// Status panel items
const statusItems = reactive([
    {label: 'Version', key: 'version', value: systemInfo.version, error: false},
    {label: 'Serial Number', key: 'serialNumber', value: '...', error: false},
    {label: 'Site ID', key: 'siteId', value: '...', error: false},
	{label: 'Remote', key: 'remote', value: '...', error: false},
	{label: 'Bar Hero', key: 'prizeEnabled', value: '...', error: false}
]);

// Message queue system
const messageQueue = ref([]);
const currentMessageId = ref(0);
const currentMessage = ref('Welcome! Performing system checks...');
const currentMessageSuccess = ref(true);
const isProcessingQueue = ref(false);
const messageInterval = ref(null);

// Steps mapping
const stepsMap = {
    'serialNumber': 0,
    'handshake': 1,
	'remote': 2,
    'prizeData': 3,
    'complete': 5 // Complete is the same as the last step
};

const totalSteps = Object.keys(stepsMap).length; // Total number of steps in the startup sequence
const completedSteps = ref(0);
const progressPercentage = computed(() => (completedSteps.value / totalSteps) * 100);

const activeStatusItems = computed(() => {
	if (startupMode.value === 'virtual-paired') {
		// Remove Serial Number, add Device ID for virtual displays
		const items = statusItems.filter(item => item.key !== 'serialNumber');
		items.splice(1, 0, {
			label: 'Display ID',
			key: 'deviceId',
			value: getDeviceId().toUpperCase().split('-')[0], // Show short version of UUID
			error: false
		});
		return items;
	}
	// Default Hardware view
	return statusItems;
});

// Update status items based on systemInfo
const updateStatusItems = () => {
    statusItems.forEach(item => {
        const systemValue = systemInfo[item.key];

        if (systemValue !== undefined) {
            if (item.key === 'prizeEnabled') {
                // Special case for Bar Hero
                item.value = systemValue ? 'Enabled' : 'Disabled';
                item.error = false;
            }

            // else if (item.key === 'serialNumber') {
            //     // Special case for Bar Hero
            //     item.value = 'ABCDE12345';
            //     item.error = false;
            // } else if (item.key === 'siteId') {
            //     // Special case for Bar Hero
            //     item.value = '12345';
            //     item.error = false;
            // }

            else {
                // General case for other items
                item.value = systemValue || 'Error';
                item.error = !systemValue;
            }


        }
    });
};

// Animation for message transitions
const onMessageEnter = (el, done) => {
    gsap.fromTo(el,
        {
            opacity: 0,
            y: 20
        },
        {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power2.out",
            onComplete: done
        }
    );
};

const onMessageLeave = (el, done) => {
    gsap.to(el, {
        opacity: 0,
        y: -20,
        duration: 0.5,
        ease: "power2.in",
        onComplete: done
    });
};

// Add a new status message to the queue
const addStatusMessage = (text, success = true) => {
    messageQueue.value.push({
        id: Date.now(),
        text,
        success
    });

    // Start processing the queue if not already processing
    if (!isProcessingQueue.value) {
        processMessageQueue();
    }
};

// Process the message queue
const processMessageQueue = () => {
    if (messageQueue.value.length === 0) {
        isProcessingQueue.value = false;
        return;
    }

    isProcessingQueue.value = true;

    // Get the next message
    const nextMessage = messageQueue.value.shift();
    currentMessageId.value = nextMessage.id;
    currentMessage.value = nextMessage.text;
    currentMessageSuccess.value = nextMessage.success;

    // Schedule the next message after 0.5 seconds if there are more
    if (messageQueue.value.length > 0) {
        setTimeout(processMessageQueue, 500);
    } else {
        isProcessingQueue.value = false;
    }
};

// Animation for initial loading sequence
function animateLoadingSequence() {
    const tl = gsap.timeline();

    // Animate logo appearing
    tl.from(loadingLogo.value, {
        scale: 0.5,
        opacity: 0,
        duration: 1.2,
        ease: "elastic.out(1, 0.5)"
    });

    return tl;
}

// Function to complete loading and transition to home screen
const completeAnimation = () => {
	gsap.to('.loading-screen', {
        opacity: 0,
        duration: 0.8,
        onComplete: () => {
            emit('loading-complete');
        }
    });
};

// Watch for changes in loading status
watch(loadingStatus, (newStatus) => {
    // Add the new status message
    addStatusMessage(newStatus, loadingSuccess.value);
});

// Watch for changes in loading step
watch(loadingStep, (newStep) => {
	if (newStep !== 'init' && !showStatusPanel.value) {
		showStatusPanel.value = true;
	}
	
    // Update progress if step is successful
    if (loadingSuccess.value && stepsMap[newStep]) {
        completedSteps.value = stepsMap[newStep];
    }
});

// Watch for changes in systemInfo
// watch(() => systemInfo.version, () => updateStatusItems());
watch(() => systemInfo.serialNumber, () => updateStatusItems());
watch(() => systemInfo.siteId, () => updateStatusItems());
watch(() => systemInfo.prizeEnabled, () => updateStatusItems());

// Expose the completeAnimation method
defineExpose({
    completeAnimation
});

onMounted(() => {
    animateLoadingSequence(); // Start loading animation
    addStatusMessage('Initializing...', true);// Add initial status message
    updateStatusItems();// Initial update of status items
});

onUnmounted(() => {
    // Clear any intervals when component is unmounted
    if (messageInterval.value) {
        clearInterval(messageInterval.value);
    }
});
</script>

<style scoped>
.loading-screen {
    position: absolute;
    top: 0;
    left: 0;
    width: calc(var(--cqw) * 100);
    height: calc(var(--cqh) * 100);
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 100;
}

/* Status panel styles */
.status-panel {
    position: absolute;
    bottom: var(--spacing-xl);
    left: var(--spacing-xl);
    border-radius: var(--border-radius-md);
    padding: var(--spacing-md);
    min-width: 20rem;
    z-index: var(--z-index-dropdown);
}

.status-item {
    display: flex;
    justify-content: space-between;
    font-size: var(--font-size-reg);
}

.status-item:not(:last-child) {
    margin-bottom: var(--spacing-sm);
}

.status-label {
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-secondary);
}

.status-value {
    padding-left: var(--spacing-xl);
}

.error-value {
    color: var(--color-error);
}

.fixed-content {
    position: absolute;
    top: 45%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
}

.loading-logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--spacing-md);
}

.logo {
    width: auto;
    height: calc(var(--cqw) * 8);
}

.loading-title {
    font-size: var(--font-size-title);
    font-weight: var(--font-weight-medium);
    /* text-shadow: 0 0 10px rgba(34, 177, 246, 0.3); */
}

.loading-bar-container {
    background-color: var(--color-border);
    width: calc(var(--cqw) * 35);
    height: var(--font-size-sm);
    border-radius: var(--border-radius-full);
    overflow: hidden;
    margin-top: var(--spacing-md);
}

.loading-bar {
    height: 100%;
    min-width: 3%;
    background: var(--gradient-primary);
    border-radius: var(--border-radius-full);
    transition: width var(--transition-loading);
}

.loading-status {
    font-size: var(--font-size-lg);
    color: var(--color-text-secondary);
}

/* Dynamic content container */
.dynamic-content {
    position: absolute;
    top: 60%;
    /* transform: translateY(50%); */
    width: 100%;
}

.status-messages-container {
    width: calc(var(--cqw) * 100);
    margin-top: var(--spacing-xl);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.message-wrapper {
    width: 100%;
    position: relative;
    /* min-height: 3rem; !* Ensure consistent height *! */
}

.status-message {
    font-size: var(--font-size-md);
    color: var(--color-text-secondary);
    width: 100%;
    text-align: center;
}

.error-message {
    color:var(--color-error);
}

/* Message transition classes */
.message-transition-enter-active,
.message-transition-leave-active {
    position: absolute;
    width: 100%;
}

/* Add to bottom of style block */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

</style>